/**
 * O que faz: Guia a criação de campanhas de follow-up em etapas simples. Ao criar campanha nova,
 *            a primeira etapa ("Modelo") mostra os modelos prontos; ao editar, começa em "Dados".
 * Onde usa: FollowupManagement ao criar ou editar campanha.
 * Conexões: hooks de campanhas, upload de mídia, WhatsApp Oficial, PresetPicker, Dialog/RadioGroup e tokens do Gens Design System.
 * Tabelas: follow_up_campaigns e follow_up_templates (INSERT/UPDATE) pelos hooks de criação e edição.
 * Autor: IA
 * Data: 30/09/2026 (apresentação e acessibilidade do assistente; etapa "Modelo" em 07/10/2026).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronLeft, ChevronRight, HelpCircle, Image as ImageIcon, LayoutTemplate, Loader2, MessageSquare, PencilLine, Plus, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { PresetPicker } from '../PresetPicker';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCreateFollowupCampaign, useFollowupAdOptions, useUpdateFollowupCampaign, useUploadFollowupImage } from '@/hooks/useFollowupData';
import { useConfigApiOficial, useTemplatesOficial } from '@/hooks/useWhatsAppOficial';
import { useEtapas } from '@/hooks/useEtapasData';
import { useClinica } from '@/contexts/ClinicaContext';
import type { FollowupCampaign, FollowupCampaignDraft, FollowupMatchType, FollowupMensagemTipo, FollowupModoEnvio, FollowupTriggerType } from '@/types/followup';
import { minutosParaLegivel } from '@/types/followup';
import { toast } from 'sonner';
// O escopo reaproveita os tokens aprovados sem alterar as outras telas do CRM.
import '@/styles/gens-escopo.css';
import './followup-wizard.css';

interface FollowupWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seedPreset: FollowupCampaign | null;
  editingCampaign: FollowupCampaign | null;
  // A cópia reaproveita o formulário, mas nunca usa o identificador da origem no salvamento.
  duplicatingCampaign?: FollowupCampaign | null;
  // targetClinicaId é passado pelo admin para criar a campanha sob a clínica selecionada, não a sua própria.
  targetClinicaId?: string | null;
  // Quando true (campanha nova), o assistente abre na etapa "Modelo" com os modelos prontos.
  // Motivo: antes os modelos apareciam soltos no meio da página, longe do botão "Nova campanha".
  escolherModelo?: boolean;
}

interface HelpPopoverProps {
  title: string;
  children: ReactNode;
}

interface DurationParts {
  dias: number;
  horas: number;
  minutos: number;
}

interface DurationEditorProps {
  idPrefix: string;
  valueMinutes: number;
  onChangeMinutes: (minutes: number) => void;
  minMinutes?: number;
}

const AI_INSTRUCTION_PRESETS = [
  {
    id: 'curta-contextual',
    titulo: 'Curta e contextual',
    descricao: 'Uma linha simples para destravar resposta.',
    prompt: [
      'Escreva um follow-up curto para WhatsApp, em apenas uma linha.',
      'Objetivo: destravar a resposta que ficou pendente, sem vender de novo.',
      'Use a ultima mensagem manual da clinica como ancora principal.',
      'Se a ultima mensagem manual pediu um dado especifico, peça somente esse dado novamente.',
      'Se a ultima mensagem manual for generica, como "me fala o que te incomoda", nao acrescente procedimento antigo.',
      'Nao avance para agendamento, valor, proposta ou procedimento se a ultima mensagem manual nao pediu isso.',
      'Evite saudacao, nome do lead, "entendi", "oi", "ola" e frases formais.',
      'Maximo de 100 caracteres, tom humano, simples e natural.',
      'Nao invente procedimento, preco, prazo, diagnostico ou beneficio.',
      'Nao use emojis.',
    ].join('\n'),
  },
  {
    id: 'consultiva',
    titulo: 'Consultiva',
    descricao: 'Ajuda o lead a decidir o próximo passo.',
    prompt: [
      'Escreva uma mensagem consultiva e objetiva.',
      'Leia o histórico e retome exatamente o assunto que ficou pendente.',
      'Mostre disponibilidade para tirar dúvida ou seguir para o próximo passo.',
      'Seja claro, sem parecer cobrança.',
      'Não invente informações que não estejam na conversa.',
      'Não use emojis.',
    ].join('\n'),
  },
  {
    id: 'curiosidade-leve',
    titulo: 'Curiosidade leve',
    descricao: 'Reabre conversa sem soar genérico.',
    prompt: [
      'Escreva uma mensagem curta usando curiosidade leve para reabrir a conversa.',
      'A curiosidade precisa estar ligada ao contexto real do histórico.',
      'Não use frases vagas como "tenho uma novidade" se isso não apareceu na conversa.',
      'Faça uma pergunta simples que facilite resposta rápida.',
      'Não invente oferta, desconto, urgência ou informação clínica.',
      'Não use emojis.',
    ].join('\n'),
  },
  {
    id: 'mais-completa',
    titulo: 'Mais completa',
    descricao: 'Um pouco mais explicativa, ainda segura.',
    prompt: [
      'Escreva uma mensagem um pouco mais completa, com no máximo 3 frases curtas.',
      'Retome o assunto real do histórico e explique por que está chamando.',
      'Finalize com uma pergunta simples para a pessoa responder.',
      'Mantenha tom humano, respeitoso e sem pressão.',
      'Não invente dados, promessas, preço, prazo ou informação médica.',
      'Não use emojis.',
    ].join('\n'),
  },
];

const parseNonNegativeInt = (value: string): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const minutesToDurationParts = (totalMinutes: number): DurationParts => {
  const safe = Math.max(0, Math.floor(totalMinutes));
  const dias = Math.floor(safe / 1440);
  const horas = Math.floor((safe % 1440) / 60);
  const minutos = safe % 60;
  return { dias, horas, minutos };
};

const durationPartsToMinutes = (parts: DurationParts, minMinutes = 0): number => {
  const total = (Math.max(0, parts.dias) * 1440) + (Math.max(0, parts.horas) * 60) + Math.max(0, parts.minutos);
  return Math.max(minMinutes, total);
};

const formatHourMinute = (date: Date) => {
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const getDynamicScheduleExample = (janelaMinutos: number, intervaloMinutos: number) => {
  // Usa um horario base fixo para facilitar entendimento visual do usuario.
  const base = new Date();
  base.setHours(14, 0, 0, 0);
  const entrouNoFollowup = new Date(base.getTime() + Math.max(0, janelaMinutos) * 60_000);
  const enviaEtapa = new Date(entrouNoFollowup.getTime() + Math.max(0, intervaloMinutos) * 60_000);

  return {
    base: formatHourMinute(base),
    entrouNoFollowup: formatHourMinute(entrouNoFollowup),
    enviaEtapa: formatHourMinute(enviaEtapa),
  };
};

const HelpPopover = ({ title, children }: HelpPopoverProps) => {
  // Mantém a explicação dentro do modal para reduzir dúvida sem tirar o usuário
  // do fluxo de criação da campanha.
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-7 sm:w-7"
          aria-label={`Ajuda sobre ${title}`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      {/* O portal da ajuda fica fora do modal e precisa receber os mesmos tokens. */}
      <PopoverContent align="start" className="gens-escopo w-80 max-w-[calc(100vw-2rem)] space-y-2 rounded-2xl text-sm">
        <p className="font-semibold text-foreground">{title}</p>
        {children}
      </PopoverContent>
    </Popover>
  );
};

const DurationEditor = ({ idPrefix, valueMinutes, onChangeMinutes, minMinutes = 0 }: DurationEditorProps) => {
  const parts = minutesToDurationParts(valueMinutes);

  const updatePart = (field: keyof DurationParts, rawValue: string) => {
    const next: DurationParts = {
      ...parts,
      [field]: parseNonNegativeInt(rawValue),
    };

    // Horas e minutos ficam normalizados para evitar valores quebrados visualmente.
    // Motivo: usuário digita livremente e o sistema precisa converter para minutos de forma estável.
    const normalizedHours = Math.min(23, next.horas);
    const normalizedMinutes = Math.min(59, next.minutos);
    onChangeMinutes(durationPartsToMinutes(
      { dias: next.dias, horas: normalizedHours, minutos: normalizedMinutes },
      minMinutes,
    ));
  };

  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="grid gap-1">
        <Label htmlFor={`${idPrefix}-dias`} className="text-xs text-muted-foreground">Dias</Label>
        <Input
          id={`${idPrefix}-dias`}
          type="number"
          min={0}
          step={1}
          value={parts.dias}
          onChange={(event) => updatePart('dias', event.target.value)}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor={`${idPrefix}-horas`} className="text-xs text-muted-foreground">Horas</Label>
        <Input
          id={`${idPrefix}-horas`}
          type="number"
          min={0}
          max={23}
          step={1}
          value={parts.horas}
          onChange={(event) => updatePart('horas', event.target.value)}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor={`${idPrefix}-minutos`} className="text-xs text-muted-foreground">Minutos</Label>
        <Input
          id={`${idPrefix}-minutos`}
          type="number"
          min={0}
          max={59}
          step={1}
          value={parts.minutos}
          onChange={(event) => updatePart('minutos', event.target.value)}
        />
      </div>
    </div>
  );
};

const createEmptyDraft = (): FollowupCampaignDraft => ({
  nome: 'Nova campanha de reativação',
  descricao: '',
  tipo: 'automatico',
  tipo_gatilho: 'inatividade_geral',
  padrao_gatilho_texto: '',
  padrao_match_type: 'contains',
  janela_inatividade_minutos: 60,
  janela_maxima_inatividade_minutos: 1440,
  horario_envio: '09:00',
  horario_limite_envio: '20:00',
  modo_envio: 'horario_fixo',
  apenas_dias_uteis: true,
  auto_pausar_ao_responder: true,
  apenas_leads_anuncio: false,
  ad_name_filtro: '',
  apenas_link_personalizado: false,
  // Começa sem segmentação por etapa para que o filtro só seja aplicado quando configurado.
  filtrar_por_etapa_kanban: false,
  etapa_kanban_id_filtro: '',
  // Campanha nova já nasce sem abordar quem comprou; etapas bloqueadas são escolha da clínica.
  etapas_bloqueadas_ids: [],
  ignorar_leads_com_venda: true,
  cooldown_dias_relancamento: 30,
  limite_diario: 50,
  usar_classificacao_inteligente: true,
  frases_sem_followup: [
    'Fico à disposição',
    'Qualquer dúvida estou por aqui',
    'Segue o link',
    'Prontinho',
    'Combinado',
  ].join('\n'),
  usa_ia: false,
  ia_prompt_base: '',
  templates: [
    {
      titulo: 'Primeira tentativa',
      conteudo: '',
      intervalo_minutos: 0,
      mensagem_tipo: 'manual',
      gerado_por_ia: false,
      imagem_object_key: '',
      imagem_bucket: '',
      imagem_mimetype: '',
      imagem_nome_arquivo: '',
      imagem_preview_url: '',
    },
  ],
});

const draftFromPreset = (preset: FollowupCampaign): FollowupCampaignDraft => ({
  nome: preset.nome.replace(/^Preset\s*-\s*/i, ''),
  descricao: preset.descricao || '',
  tipo: 'automatico',
  tipo_gatilho: preset.tipo_gatilho,
  padrao_gatilho_texto: preset.padrao_gatilho_texto || '',
  padrao_match_type: preset.padrao_match_type || 'contains',
  janela_inatividade_minutos: preset.janela_inatividade_minutos,
  janela_maxima_inatividade_minutos: preset.janela_maxima_inatividade_minutos || Math.max(1440, preset.janela_inatividade_minutos),
  horario_envio: (preset.horario_envio || '09:00').slice(0, 5),
  horario_limite_envio: (preset.horario_limite_envio || '20:00').slice(0, 5),
  modo_envio: preset.modo_envio || 'horario_fixo',
  apenas_dias_uteis: Boolean(preset.apenas_dias_uteis),
  auto_pausar_ao_responder: Boolean(preset.auto_pausar_ao_responder),
  apenas_leads_anuncio: Boolean(preset.apenas_leads_anuncio),
  ad_name_filtro: preset.ad_name_filtro || '',
  apenas_link_personalizado: Boolean(preset.apenas_link_personalizado),
  // Preserva a segmentação do preset somente quando ele já possui uma etapa válida.
  filtrar_por_etapa_kanban: Boolean(preset.etapa_kanban_id_filtro),
  etapa_kanban_id_filtro: preset.etapa_kanban_id_filtro || '',
  // Preset é global: etapas são de cada clínica, então começa sem etapa bloqueada.
  etapas_bloqueadas_ids: [],
  ignorar_leads_com_venda: preset.ignorar_leads_com_venda ?? true,
  cooldown_dias_relancamento: preset.cooldown_dias_relancamento || 30,
  limite_diario: preset.limite_diario || 50,
  usar_classificacao_inteligente: preset.usar_classificacao_inteligente ?? true,
  frases_sem_followup: (preset.frases_sem_followup || []).join('\n'),
  usa_ia: Boolean(preset.usa_ia || preset.follow_up_templates?.some((template) => template.gerado_por_ia)),
  ia_prompt_base: preset.ia_prompt_base || '',
  templates: (preset.follow_up_templates || [])
    .sort((a, b) => a.sequencia - b.sequencia)
    .map((template) => ({
      id: template.id,
      titulo: template.titulo,
      conteudo: template.gerado_por_ia ? '' : template.conteudo,
      intervalo_minutos: template.intervalo_minutos,
      mensagem_tipo: template.gerado_por_ia ? ('ai_dinamico' as FollowupMensagemTipo) : ('manual' as FollowupMensagemTipo),
      gerado_por_ia: Boolean(template.gerado_por_ia),
      imagem_object_key: template.imagem_object_key || '',
      imagem_bucket: template.imagem_bucket || '',
      imagem_mimetype: template.imagem_mimetype || '',
      imagem_nome_arquivo: template.imagem_nome_arquivo || '',
      imagem_preview_url: '',
    })),
});

const draftFromCampaign = (campaign: FollowupCampaign): FollowupCampaignDraft => ({
  nome: campaign.nome,
  descricao: campaign.descricao || '',
  tipo: campaign.tipo,
  tipo_gatilho: campaign.tipo_gatilho,
  padrao_gatilho_texto: campaign.padrao_gatilho_texto || '',
  padrao_match_type: campaign.padrao_match_type || 'contains',
  janela_inatividade_minutos: campaign.janela_inatividade_minutos,
  janela_maxima_inatividade_minutos: campaign.janela_maxima_inatividade_minutos || Math.max(1440, campaign.janela_inatividade_minutos),
  horario_envio: (campaign.horario_envio || '09:00').slice(0, 5),
  horario_limite_envio: (campaign.horario_limite_envio || '20:00').slice(0, 5),
  modo_envio: campaign.modo_envio || 'horario_fixo',
  apenas_dias_uteis: Boolean(campaign.apenas_dias_uteis),
  auto_pausar_ao_responder: Boolean(campaign.auto_pausar_ao_responder),
  apenas_leads_anuncio: Boolean(campaign.apenas_leads_anuncio),
  ad_name_filtro: campaign.ad_name_filtro || '',
  apenas_link_personalizado: Boolean(campaign.apenas_link_personalizado),
  // Mantém o filtro salvo ao editar uma campanha para não remover uma regra sem intenção.
  filtrar_por_etapa_kanban: Boolean(campaign.etapa_kanban_id_filtro),
  etapa_kanban_id_filtro: campaign.etapa_kanban_id_filtro || '',
  etapas_bloqueadas_ids: campaign.etapas_bloqueadas_ids || [],
  ignorar_leads_com_venda: campaign.ignorar_leads_com_venda ?? true,
  cooldown_dias_relancamento: campaign.cooldown_dias_relancamento || 0,
  limite_diario: campaign.limite_diario || 50,
  usar_classificacao_inteligente: campaign.usar_classificacao_inteligente ?? true,
  frases_sem_followup: (campaign.frases_sem_followup || []).join('\n'),
  usa_ia: Boolean(campaign.usa_ia),
  ia_prompt_base: campaign.ia_prompt_base || '',
  templates: (campaign.follow_up_templates || [])
    .filter((template) => template.ativo && !template.archived_at)
    .sort((a, b) => a.sequencia - b.sequencia)
    .map((template) => ({
      id: template.id,
      titulo: template.titulo,
      conteudo: template.gerado_por_ia ? '' : template.conteudo,
      intervalo_minutos: template.intervalo_minutos,
      mensagem_tipo: template.gerado_por_ia ? ('ai_dinamico' as FollowupMensagemTipo) : ('manual' as FollowupMensagemTipo),
      gerado_por_ia: Boolean(template.gerado_por_ia),
      imagem_object_key: template.imagem_object_key || '',
      imagem_bucket: template.imagem_bucket || '',
      imagem_mimetype: template.imagem_mimetype || '',
      imagem_nome_arquivo: template.imagem_nome_arquivo || '',
      imagem_preview_url: '',
      // Preserva a mensagem aprovada e suas variáveis ao editar ou duplicar pelo WhatsApp Oficial.
      template_oficial_id: template.template_oficial_id || '',
      mapeamento_variaveis: [...(template.mapeamento_variaveis || [])],
    })),
});

export const FollowupWizard = ({ open, onOpenChange, seedPreset, editingCampaign, duplicatingCampaign, targetClinicaId, escolherModelo = false }: FollowupWizardProps) => {
  // Etapa 0 = "Modelo" (só existe ao criar com escolherModelo); 1 a 4 = Dados, Regras, Mensagens, Revisão.
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<FollowupCampaignDraft>(createEmptyDraft);
  // Nome do modelo usado como base, para lembrar a origem no topo de "Dados" e permitir trocar.
  const [modeloEscolhido, setModeloEscolhido] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const createCampaign = useCreateFollowupCampaign(targetClinicaId);
  const updateCampaign = useUpdateFollowupCampaign();
  const uploadFollowupImage = useUploadFollowupImage();
  const { data: adOptions = [] } = useFollowupAdOptions(open);
  const { clinicaId: clinicaContexto } = useClinica();
  const clinicaIdWizard = targetClinicaId ?? clinicaContexto ?? undefined;
  const { data: configOficial } = useConfigApiOficial(clinicaIdWizard);
  const { data: templatesOficiais = [] } = useTemplatesOficial(clinicaIdWizard);
  // Etapas do funil da clínica da campanha (inclusive quando o admin edita outra clínica).
  // Usadas para escolher quais etapas nunca recebem esta campanha.
  const { data: etapasClinica = [] } = useEtapas(clinicaIdWizard, { enabled: open });
  const oficialAtivo = configOficial?.config?.ativo === true;
  const templatesAprovados = templatesOficiais.filter((item) => item.status === 'APPROVED');
  // Índice 0 é a etapa "Modelo"; os textos das etapas 1 a 4 não mudaram.
  const stepLabels = ['Modelo', 'Dados', 'Regras', 'Mensagens', 'Revisão'];
  const stepDescriptions = [
    'Comece por um modelo pronto. Tudo pode ser ajustado nas próximas etapas.',
    'Identifique a campanha e escolha quando retomar a conversa.',
    'Defina os horários, o público e os limites de envio.',
    'Monte a sequência e escolha como cada mensagem será escrita.',
    'Confira as regras e a sequência antes de salvar a campanha.',
  ];
  const isEditing = Boolean(editingCampaign?.id);
  // A etapa "Modelo" só aparece na criação; na edição a campanha já tem regras e mensagens.
  const temEtapaModelo = escolherModelo && !isEditing && !seedPreset && !duplicatingCampaign;
  const primeiraEtapa = temEtapaModelo ? 0 : 1;
  const etapasVisiveis = temEtapaModelo ? [0, 1, 2, 3, 4] : [1, 2, 3, 4];
  const selectedAdOption = adOptions.find((option) => option.originalName === draft.ad_name_filtro) || null;

  useEffect(() => {
    if (!open) return;
    setStep(temEtapaModelo ? 0 : 1);
    setModeloEscolhido(seedPreset?.nome ?? null);
    if (editingCampaign) {
      setDraft(draftFromCampaign(editingCampaign));
      return;
    }
    if (duplicatingCampaign) {
      // Reutiliza somente configurações: IDs e histórico pertencem à origem, e a criação salva inativa.
      const copia = draftFromCampaign(duplicatingCampaign);
      setDraft({
        ...copia,
        nome: `${duplicatingCampaign.nome} (cópia)`,
        templates: copia.templates.map(({ id: _id, ...template }) => template),
      });
      return;
    }
    setDraft(seedPreset ? draftFromPreset(seedPreset) : createEmptyDraft());
  }, [open, seedPreset, editingCampaign, duplicatingCampaign, temEtapaModelo]);

  // Escolher um modelo preenche o rascunho e avança direto para "Dados".
  const escolherModeloPronto = (modelo: FollowupCampaign) => {
    setDraft(draftFromPreset(modelo));
    setModeloEscolhido(modelo.nome);
    setStep(1);
  };

  // "Começar do zero" limpa qualquer modelo escolhido antes, para não herdar regras sem querer.
  const comecarDoZero = () => {
    setDraft(createEmptyDraft());
    setModeloEscolhido(null);
    setStep(1);
  };

  const isStepValid = useMemo(() => {
    if (step === 1) return draft.nome.trim().length >= 3;
    if (step === 2) return draft.janela_inatividade_minutos > 0 && draft.cooldown_dias_relancamento >= 0 && draft.limite_diario > 0;
    if (step === 3) return draft.templates.some((t) => t.mensagem_tipo === 'ai_dinamico' || t.conteudo.trim() || t.imagem_object_key);
    return true;
  }, [draft, step]);

  useEffect(() => {
    // Cada etapa começa no topo; o CSS cuida do overflow sem medir altura a cada edição.
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [step]);

  const updateTemplate = (index: number, patch: Partial<FollowupCampaignDraft['templates'][number]>) => {
    setDraft((current) => ({
      ...current,
      templates: current.templates.map((template, templateIndex) =>
        templateIndex === index ? { ...template, ...patch } : template,
      ),
    }));
  };

  const addTemplate = () => {
    setDraft((current) => ({
      ...current,
      templates: [
        ...current.templates,
        {
          titulo: `Mensagem ${current.templates.length + 1}`,
          conteudo: '',
          intervalo_minutos: 1440,
          mensagem_tipo: 'manual' as FollowupMensagemTipo,
          gerado_por_ia: false,
          imagem_object_key: '',
          imagem_bucket: '',
          imagem_mimetype: '',
          imagem_nome_arquivo: '',
          imagem_preview_url: '',
        },
      ],
    }));
  };

  const removeTemplate = (index: number) => {
    setDraft((current) => ({
      ...current,
      templates: current.templates.filter((_, templateIndex) => templateIndex !== index),
    }));
  };

  const updateMensagemTipo = (index: number, tipo: FollowupMensagemTipo) => {
    const isAi = tipo === 'ai_dinamico';
    updateTemplate(index, {
      mensagem_tipo: tipo,
      gerado_por_ia: isAi,
      conteudo: isAi ? '' : draft.templates[index].conteudo,
    });
    setDraft((current) => ({
      ...current,
      usa_ia: isAi || current.templates.some((t, i) => i !== index && t.mensagem_tipo === 'ai_dinamico'),
    }));
  };

  const uploadTemplateImage = async (index: number, file: File | null) => {
    if (!file) return;

    // Envia a imagem antes de salvar a campanha.
    // Motivo: a campanha precisa guardar uma referencia permanente para reenviar a imagem depois.
    const uploadedImage = await uploadFollowupImage.mutateAsync(file);
    updateTemplate(index, {
      imagem_object_key: uploadedImage.objectKey,
      imagem_bucket: uploadedImage.bucket,
      imagem_mimetype: uploadedImage.mimeType,
      imagem_nome_arquivo: uploadedImage.fileName,
      imagem_preview_url: uploadedImage.signedUrl,
    });
  };

  const removeTemplateImage = (index: number) => {
    // Remove a referencia da etapa sem apagar o arquivo ja enviado.
    // Motivo: evita perda acidental de arquivo enquanto a campanha ainda pode estar em edicao/historico.
    updateTemplate(index, {
      imagem_object_key: '',
      imagem_bucket: '',
      imagem_mimetype: '',
      imagem_nome_arquivo: '',
      imagem_preview_url: '',
    });
  };

  const handleSave = async () => {
    // Bloqueia uma janela invertida porque o sistema precisa saber claramente
    // a partir de que hora pode enviar e ate que hora ainda e permitido.
    if (draft.horario_limite_envio < draft.horario_envio) {
      toast.error('O horário limite precisa ser igual ou depois do horário inicial.');
      return;
    }

    // Bloqueia intervalo maximo menor que o minimo para evitar uma campanha impossivel.
    // Motivo: o lead precisa estar entre o minimo e o maximo sem resposta para ser elegivel.
    if (draft.janela_maxima_inatividade_minutos <= draft.janela_inatividade_minutos) {
      toast.error('O limite maximo sem resposta precisa ser maior que o tempo minimo.');
      return;
    }

    try {
      if (editingCampaign?.id) {
        await updateCampaign.mutateAsync({ id: editingCampaign.id, draft });
      } else {
        await createCampaign.mutateAsync(draft);
      }
      onOpenChange(false);
    } catch {
      // O hook já mostra o erro em português; manter o catch evita erro não tratado no clique.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A animação fade + scale vem de DialogContent; só o corpo possui rolagem. */}
      <DialogContent className="gens-escopo followup-wizard flex h-[min(860px,92dvh)] w-[calc(100%-1rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-3xl p-0 shadow-[var(--lg-sombra-2)] sm:w-[calc(100%-3rem)] sm:rounded-3xl">
        <div className="shrink-0 border-b bg-card px-4 pb-4 pt-5 sm:px-6 sm:pt-6">
          <DialogHeader className="mb-5 pr-8 text-left">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-brand-strong">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Follow-up
            </div>
            <DialogTitle className="font-display text-lg leading-snug sm:text-xl">
              {isEditing ? 'Editar campanha de follow-up' : duplicatingCampaign ? 'Duplicar campanha de follow-up' : 'Criar campanha de follow-up'}
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              {duplicatingCampaign
                ? 'Revise a cópia antes de salvar. Ela será criada pausada, sem histórico de envios.'
                : 'Retome conversas com uma sequência de mensagens no momento certo.'}
            </DialogDescription>
          </DialogHeader>

          {/* Número, texto e visto comunicam o progresso também sem percepção de cor. */}
          <ol aria-label="Etapas da campanha" className={`grid gap-1 sm:gap-2 ${temEtapaModelo ? 'grid-cols-5' : 'grid-cols-4'}`}>
            {etapasVisiveis.map((item, posicao) => {
              const isActive = item === step;
              const isCompleted = item < step;
              return (
                <li key={item} aria-current={isActive ? 'step' : undefined} className={`flex min-w-0 flex-col items-center gap-1.5 rounded-2xl px-1 py-2 text-xs transition-colors duration-200 motion-reduce:transition-none sm:flex-row sm:gap-2 sm:px-3 ${isActive ? 'bg-accent font-semibold text-accent-foreground' : 'text-muted-foreground'}`}>
                  <span aria-hidden="true" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${isActive ? 'border-primary bg-primary text-primary-foreground' : isCompleted ? 'border-border bg-secondary text-brand-strong' : 'border-border bg-card'}`}>
                    {isCompleted ? <Check className="h-3.5 w-3.5" /> : posicao + 1}
                  </span>
                  <span className="truncate">{stepLabels[item]}{isCompleted && <span className="sr-only">: concluída</span>}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Fade nativo no dono do scroll: sem listeners, polling ou estado visual. */}
        <div ref={contentRef} className="crm-scroll crm-scroll-fade min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface px-4 py-5 sm:px-6 sm:py-6">
          {/* A key troca a cada etapa: o conteúdo novo entra com fade + deslize curto (só opacity/transform). */}
          <div key={step} className="animate-in fade-in-0 slide-in-from-right-2 duration-200 motion-reduce:animate-none">
          <div className="mb-6 space-y-1" aria-live="polite" aria-atomic="true">
            <p className="lg-rotulo">Etapa {etapasVisiveis.indexOf(step) + 1} de {etapasVisiveis.length}</p>
            <h3 className="text-base">{stepLabels[step]}</h3>
            <p className="text-sm text-muted-foreground">{stepDescriptions[step]}</p>
          </div>

        {step === 0 && <PresetPicker onSelect={escolherModeloPronto} />}

        {step === 1 && (
          <div className="followup-sections space-y-6">
            {/* Lembra de qual modelo a campanha veio e deixa voltar para trocar, sem perder o fluxo. */}
            {temEtapaModelo && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-accent/60 px-4 py-3 text-[13px] text-accent-foreground">
                <span className="flex min-w-0 items-center gap-2">
                  <LayoutTemplate className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0">
                    {modeloEscolhido ? <>Baseada no modelo <strong className="font-semibold">{modeloEscolhido}</strong></> : 'Campanha começando do zero'}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="rounded-full px-2 py-1 font-semibold underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Trocar modelo
                </button>
              </div>
            )}
            {/* Seção: Identificação */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Identificação</h3>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="followup-nome" className="text-sm font-semibold">Nome da campanha</Label>
                  <Input
                    id="followup-nome"
                    value={draft.nome}
                    onChange={(event) => setDraft({ ...draft, nome: event.target.value })}
                    placeholder="Ex: Reativação de orçamento"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="followup-descricao" className="text-sm font-semibold">Descrição</Label>
                  <Textarea
                    id="followup-descricao"
                    value={draft.descricao}
                    onChange={(event) => setDraft({ ...draft, descricao: event.target.value })}
                    placeholder="Ex: reativar leads que pediram orçamento e não responderam."
                    className="bg-card"
                  />
                </div>
              </div>
            </div>

            {/* Seção: Gatilho de ativação */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Gatilho de ativação</h3>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-semibold">Quando enviar?</Label>
                    <HelpPopover title="Quando o follow-up deve entrar">
                      <div className="space-y-2 text-muted-foreground">
                        <p><strong className="text-foreground">Lead não respondeu minha mensagem:</strong> use quando a clínica enviou uma mensagem e o lead ficou sem responder pelo tempo configurado.</p>
                        <p><strong className="text-foreground">Minha pergunta ficou sem resposta:</strong> use quando a última mensagem termina com pergunta e você quer retomar a conversa.</p>
                        <p><strong className="text-foreground">Mensagem específica sem resposta:</strong> use para orçamento, agenda ou proposta; o sistema procura uma frase exata ou parecida enviada pela clínica.</p>
                        <p><strong className="text-foreground">Envio manual:</strong> não dispara sozinho; use apenas quando um atendente escolher enviar.</p>
                      </div>
                    </HelpPopover>
                  </div>
                  <Select
                    value={draft.tipo_gatilho}
                    onValueChange={(value: FollowupTriggerType) => setDraft({ ...draft, tipo_gatilho: value })}
                  >
                    <SelectTrigger className="bg-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="gens-escopo rounded-2xl">
                      <SelectItem value="inatividade_geral">Lead não respondeu minha mensagem</SelectItem>
                      <SelectItem value="pergunta_sem_resposta">Minha pergunta ficou sem resposta</SelectItem>
                      <SelectItem value="mensagem_marcada">Mensagem específica sem resposta</SelectItem>
                      <SelectItem value="manual">Envio manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {draft.tipo_gatilho === 'mensagem_marcada' && (
                  <div className="space-y-3 rounded-2xl border border-border bg-muted p-3">
                    <div className="grid gap-2">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="followup-padrao" className="text-sm font-semibold">Frase enviada pela clínica</Label>
                        <HelpPopover title="Frase enviada pela clínica">
                          <p className="text-muted-foreground">
                            Use uma frase que costuma aparecer quando existe um contexto específico, como "segue o orçamento" ou "tenho horário". O sistema procura essa mensagem enviada pela empresa e acompanha se o lead não respondeu.
                          </p>
                        </HelpPopover>
                      </div>
                      <Input
                        id="followup-padrao"
                        value={draft.padrao_gatilho_texto}
                        onChange={(event) => setDraft({ ...draft, padrao_gatilho_texto: event.target.value })}
                        placeholder="Ex: segue o orçamento"
                        className="bg-card"
                      />
                    </div>
                    {draft.padrao_gatilho_texto.trim() && (
                      <RadioGroup
                        value={draft.padrao_match_type}
                        onValueChange={(v: FollowupMatchType) => setDraft({ ...draft, padrao_match_type: v })}
                        className="grid gap-3 sm:grid-cols-2"
                      >
                        {(
                          [
                            { value: 'contains', label: 'Contém a frase', desc: 'quando a mensagem inclui esse texto' },
                            { value: 'equals',   label: 'É exatamente essa frase', desc: 'quando a mensagem é igual, sem mais nada' },
                          ] as { value: FollowupMatchType; label: string; desc: string }[]
                        ).map(({ value, label, desc }) => (
                          <label
                            key={value}
                            htmlFor={`match-${value}`}
                            className={`flex flex-1 cursor-pointer items-start gap-2.5 rounded-2xl border p-3 text-sm transition-colors ${
                              draft.padrao_match_type === value
                                ? 'border-ring bg-accent text-foreground'
                                : 'border-input bg-card text-foreground hover:border-input'
                            }`}
                          >
                            <RadioGroupItem value={value} id={`match-${value}`} className="mt-0.5 shrink-0" />
                            <span>
                              <strong className="block font-medium">{label}</strong>
                              <span className="text-xs text-muted-foreground">{desc}</span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="followup-sections space-y-6">
            {/* Seção: Estratégia de disparo */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Estratégia de disparo</h3>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-semibold">Modo de envio</Label>
                    <HelpPopover title="Estratégia de disparo">
                      <div className="space-y-2 text-muted-foreground">
                        <p><strong className="text-foreground">Horário fixo diário:</strong> verifica quem já está apto e envia no ciclo do horário configurado, como todo dia às 09:00.</p>
                        <p><strong className="text-foreground">Após a mensagem:</strong> envia quando completar o tempo sem resposta, como 3 horas depois da última mensagem da clínica.</p>
                      </div>
                    </HelpPopover>
                  </div>
                  <RadioGroup
                    value={draft.modo_envio}
                    onValueChange={(value: FollowupModoEnvio) => setDraft({ ...draft, modo_envio: value })}
                    className="grid gap-2 sm:grid-cols-2"
                  >
                    {([
                      { value: 'horario_fixo', label: 'Horário fixo diário', desc: 'Ex.: todo dia às 09:00 para quem já estiver apto.' },
                      { value: 'apos_mensagem', label: 'Após a mensagem', desc: 'Ex.: enviar assim que completar 3h sem resposta.' },
                    ] as { value: FollowupModoEnvio; label: string; desc: string }[]).map(({ value, label, desc }) => (
                      <label
                        key={value}
                        htmlFor={`modo-envio-${value}`}
                        className={`flex cursor-pointer items-start gap-2.5 rounded-2xl border p-3 text-sm transition-colors ${
                          draft.modo_envio === value
                            ? 'border-ring bg-accent text-foreground'
                            : 'border-input bg-card text-foreground hover:border-input'
                        }`}
                      >
                        <RadioGroupItem value={value} id={`modo-envio-${value}`} className="mt-0.5 shrink-0" />
                        <span>
                          <strong className="block font-medium">{label}</strong>
                          <span className="text-xs text-muted-foreground">{desc}</span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="followup-janela" className="text-sm font-semibold">
                      {draft.modo_envio === 'apos_mensagem' ? 'Enviar após quanto tempo?' : 'Lead apto após quanto tempo?'}
                    </Label>
                    <DurationEditor
                      idPrefix="followup-janela"
                      valueMinutes={draft.janela_inatividade_minutos}
                      minMinutes={1}
                      onChangeMinutes={(minutes) => setDraft({ ...draft, janela_inatividade_minutos: minutes })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="followup-janela-maxima" className="text-sm font-semibold">Não incluir leads sem resposta há mais de</Label>
                      <HelpPopover title="Limite máximo sem resposta">
                        <p className="text-muted-foreground">
                          Evita que campanha curta envie para conversas antigas. Ex.: mínimo 15 min e máximo 24 h pega somente leads entre 15 min e 24 h sem resposta.
                        </p>
                      </HelpPopover>
                    </div>
                    <DurationEditor
                      idPrefix="followup-janela-maxima"
                      valueMinutes={draft.janela_maxima_inatividade_minutos}
                      minMinutes={draft.janela_inatividade_minutos + 1}
                      onChangeMinutes={(minutes) => setDraft({ ...draft, janela_maxima_inatividade_minutos: minutes })}
                    />
                  </div>
                  <div className={`grid gap-2 ${draft.modo_envio === 'apos_mensagem' ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="followup-horario" className="text-sm font-semibold">Horário fixo de envio</Label>
                      <HelpPopover title="Horário fixo de envio">
                        <p className="text-muted-foreground">
                          Usado somente na estratégia de horário fixo. O cron roda a cada 5 minutos, então um horário 09:00 envia no primeiro ciclo logo após 09:00 para leads que já estiverem aptos.
                        </p>
                      </HelpPopover>
                    </div>
                    <Input
                      id="followup-horario"
                      type="time"
                      value={draft.horario_envio}
                      disabled={draft.modo_envio === 'apos_mensagem'}
                      onChange={(event) => setDraft({ ...draft, horario_envio: event.target.value })}
                      className="bg-card"
                    />
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="followup-horario-limite" className="text-sm font-semibold">Horario limite da noite</Label>
                      <HelpPopover title="Horario limite da noite">
                        <p className="text-muted-foreground">
                          Depois deste horario, o sistema segura o follow-up para a proxima janela permitida e evita disparo de madrugada.
                        </p>
                      </HelpPopover>
                    </div>
                    <Input
                      id="followup-horario-limite"
                      type="time"
                      value={draft.horario_limite_envio}
                      onChange={(event) => setDraft({ ...draft, horario_limite_envio: event.target.value })}
                      className="bg-card"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-3 text-sm text-blue-800">
                  {draft.modo_envio === 'apos_mensagem'
                    ? `Neste modo, o follow-up pode sair entre ${minutosParaLegivel(draft.janela_inatividade_minutos)} e ${minutosParaLegivel(draft.janela_maxima_inatividade_minutos)} sem resposta, mas nunca depois de ${draft.horario_limite_envio}.`
                    : `Neste modo, o lead fica apto entre ${minutosParaLegivel(draft.janela_inatividade_minutos)} e ${minutosParaLegivel(draft.janela_maxima_inatividade_minutos)} sem resposta; o envio abre em ${draft.horario_envio} e fecha em ${draft.horario_limite_envio}.`}
                </div>
              </div>
            </div>

            {/* Seção: Comportamento */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Comportamento</h3>
              <div className="space-y-3 rounded-2xl border border-border bg-muted p-4">
                <div className="flex items-center justify-between rounded-2xl border bg-card p-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">Apenas dias úteis</p>
                      <HelpPopover title="Apenas dias úteis">
                        <p className="text-muted-foreground">
                          Quando ligado, o sistema não envia follow-up no sábado ou domingo. Se uma etapa vencer no fim de semana, ela espera o próximo ciclo permitido.
                        </p>
                      </HelpPopover>
                    </div>
                    <p className="text-xs text-muted-foreground">Evita disparos no fim de semana.</p>
                  </div>
                  <Switch
                    aria-label="Enviar apenas em dias úteis"
                    className="shrink-0"
                    checked={draft.apenas_dias_uteis}
                    onCheckedChange={(checked) => setDraft({ ...draft, apenas_dias_uteis: checked })}
                  />
                </div>

                <div className="flex items-center justify-between rounded-2xl border bg-card p-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">Auto-pausar quando o lead responder</p>
                      <HelpPopover title="Auto-pausar quando o lead responder">
                        <p className="text-muted-foreground">
                          Cancela as próximas mensagens pendentes desta sequência assim que o lead responder. Uma nova pergunta da empresa pode iniciar uma nova sequência depois.
                        </p>
                      </HelpPopover>
                    </div>
                    <p className="text-xs text-muted-foreground">Cancela as próximas etapas pendentes.</p>
                  </div>
                  <Switch
                    aria-label="Auto-pausar quando o lead responder"
                    className="shrink-0"
                    checked={draft.auto_pausar_ao_responder}
                    onCheckedChange={(checked) => setDraft({ ...draft, auto_pausar_ao_responder: checked })}
                  />
                </div>

                {/* Público bloqueado: conferido quando o follow-up é criado e de novo na hora do envio. */}
                <div className="flex items-center justify-between rounded-2xl border bg-card p-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">Não enviar para quem já comprou</p>
                      <HelpPopover title="Não enviar para quem já comprou">
                        <p className="text-muted-foreground">
                          Leads com venda registrada no CRM não recebem esta campanha. Se a venda for registrada enquanto o follow-up espera na fila, ele é cancelado antes de sair.
                        </p>
                      </HelpPopover>
                    </div>
                    <p className="text-xs text-muted-foreground">Evita mandar mensagem de venda para cliente.</p>
                  </div>
                  <Switch
                    aria-label="Não enviar para quem já comprou"
                    className="shrink-0"
                    checked={draft.ignorar_leads_com_venda}
                    onCheckedChange={(checked) => setDraft({ ...draft, ignorar_leads_com_venda: checked })}
                  />
                </div>

                <div className="grid gap-2 rounded-2xl border bg-card p-3">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold">Não enviar para leads nestas etapas</p>
                    <HelpPopover title="Etapas bloqueadas">
                      <p className="text-muted-foreground">
                        Marque etapas como venda concluída, clientes, recorrentes ou desqualificados. Se o lead estiver ou for movido para uma delas, o follow-up é cancelado.
                      </p>
                    </HelpPopover>
                  </div>
                  {etapasClinica.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhuma etapa do funil encontrada para esta clínica.</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {etapasClinica.map((etapa) => {
                        const marcada = draft.etapas_bloqueadas_ids.includes(etapa.id);
                        return (
                          <label
                            key={etapa.id}
                            htmlFor={`followup-etapa-bloqueada-${etapa.id}`}
                            className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm"
                          >
                            <Checkbox
                              id={`followup-etapa-bloqueada-${etapa.id}`}
                              checked={marcada}
                              onCheckedChange={(checked) => setDraft({
                                ...draft,
                                // Mantém a lista sem duplicatas para o banco receber exatamente o que está marcado.
                                etapas_bloqueadas_ids: checked
                                  ? Array.from(new Set([...draft.etapas_bloqueadas_ids, etapa.id]))
                                  : draft.etapas_bloqueadas_ids.filter((id) => id !== etapa.id),
                              })}
                            />
                            <span className="truncate">{etapa.nome}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Se um atendente mandar mensagem para o lead, a sequência em andamento também é cancelada automaticamente.
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl border bg-card p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold">Follow-up inteligente (recomendado ativar)</p>
                        <HelpPopover title="Follow-up inteligente">
                          <div className="space-y-2 text-muted-foreground">
                            <p>
                              Antes de iniciar uma nova sequência, o sistema avalia se a última mensagem manual da clínica realmente pede resposta do lead.
                            </p>
                            <p>
                              Isso evita follow-up depois de mensagens como "bom dia", "obrigado", "fico à disposição", "segue o link" ou recados puramente informativos.
                            </p>
                            <p>
                              Se a mensagem tiver um pedido claro, como "qual horário fica melhor?" ou "pode me confirmar?", o follow-up continua permitido.
                            </p>
                          </div>
                        </HelpPopover>
                      </div>
                      <p className="text-xs text-muted-foreground">Evita disparos quando a mensagem não precisa de resposta.</p>
                    </div>
                    <Switch
                      aria-label="Follow-up inteligente"
                      className="shrink-0"
                      checked={draft.usar_classificacao_inteligente}
                      onCheckedChange={(checked) => setDraft({ ...draft, usar_classificacao_inteligente: checked })}
                    />
                  </div>

                  {draft.usar_classificacao_inteligente && (
                    <div className="grid gap-2">
                      <Label htmlFor="followup-frases-sem-resposta" className="text-sm font-semibold">
                        Frases que não precisam de follow-up
                      </Label>
                      <Textarea
                        id="followup-frases-sem-resposta"
                        value={draft.frases_sem_followup}
                        onChange={(event) => setDraft({ ...draft, frases_sem_followup: event.target.value })}
                        placeholder="Uma frase por linha. Ex: fico à disposição"
                        className="min-h-24 bg-card"
                      />
                      <p className="text-xs text-muted-foreground">
                        Use uma frase por linha. Se a última mensagem manual contiver uma dessas frases e não tiver pedido claro de resposta, o follow-up não inicia.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between rounded-2xl border bg-card p-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">Apenas leads de anúncios</p>
                      <HelpPopover title="Apenas leads de anúncios">
                        <p className="text-muted-foreground">
                          Usa somente leads com a coluna anuncio preenchida. É útil para cadências curtas, porque esse lead costuma estar mais quente logo após clicar no anúncio.
                        </p>
                        <p className="text-muted-foreground">
                          Se este toggle e o de link personalizado estiverem ligados juntos, basta o lead ter uma das duas origens.
                        </p>
                      </HelpPopover>
                    </div>
                    <p className="text-xs text-muted-foreground">Filtra para leads com a coluna anuncio marcada.</p>
                  </div>
                  <Switch
                    aria-label="Apenas leads de anúncios"
                    className="shrink-0"
                    checked={draft.apenas_leads_anuncio}
                    onCheckedChange={(checked) => setDraft({
                      ...draft,
                      apenas_leads_anuncio: checked,
                      // Limpa o anuncio especifico quando o filtro geral e desligado.
                      // Motivo: evitar campanha escondendo um filtro antigo que o usuario nao esta vendo.
                      ad_name_filtro: checked ? draft.ad_name_filtro : '',
                    })}
                  />
                </div>

                {draft.apenas_leads_anuncio && (
                  <div className="grid gap-2 rounded-2xl border bg-card p-3">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="followup-ad-name-filtro" className="text-sm font-semibold">Anúncio específico</Label>
                      <HelpPopover title="Anúncio específico">
                        <p className="text-muted-foreground">
                          Selecione um anúncio para restringir a campanha apenas aos leads desse criativo.
                        </p>
                        <p className="text-muted-foreground">
                          Se deixar em "Qualquer anúncio", a campanha continua aceitando todos os leads com ad_name preenchido.
                        </p>
                      </HelpPopover>
                    </div>
                    <Select
                      value={draft.ad_name_filtro || '__all_ads__'}
                      onValueChange={(value) => setDraft({
                        ...draft,
                        // Mantem vazio quando o usuario quer qualquer anuncio.
                        // Motivo: o banco usa null para o caso generico e texto para o filtro especifico.
                        ad_name_filtro: value === '__all_ads__' ? '' : value,
                      })}
                    >
                      <SelectTrigger id="followup-ad-name-filtro" className="bg-card">
                        <SelectValue placeholder="Escolha um anúncio" />
                      </SelectTrigger>
                      <SelectContent className="gens-escopo rounded-2xl">
                        <SelectItem value="__all_ads__">Qualquer anúncio</SelectItem>
                        {adOptions.map((adOption) => (
                          <SelectItem key={adOption.originalName} value={adOption.originalName}>
                            {adOption.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {adOptions.length > 0
                        ? 'A lista usa os nomes de anúncios já encontrados nos leads e mostra o apelido quando ele estiver cadastrado.'
                        : 'Ainda não encontramos leads com ad_name preenchido. A campanha pode continuar em "qualquer anúncio" até chegar tráfego identificado.'}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between rounded-2xl border bg-card p-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">Apenas com link personalizado</p>
                      <HelpPopover title="Apenas com link personalizado">
                        <p className="text-muted-foreground">
                          Usa somente leads com link_name preenchido. Serve para campanhas de links específicos, como anúncio, influenciador, campanha sazonal ou página dedicada.
                        </p>
                        <p className="text-muted-foreground">
                          Se este toggle e o de anúncios estiverem ligados juntos, basta o lead ter anuncio ou link_name preenchido.
                        </p>
                      </HelpPopover>
                    </div>
                    <p className="text-xs text-muted-foreground">Filtra para leads com link_name preenchido.</p>
                  </div>
                  <Switch
                    aria-label="Apenas links personalizados"
                    className="shrink-0"
                    checked={draft.apenas_link_personalizado}
                    onCheckedChange={(checked) => setDraft({ ...draft, apenas_link_personalizado: checked })}
                  />
                </div>
              </div>
            </div>

            {/* Seção: Limites de segurança */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Limites de segurança</h3>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="followup-cooldown" className="text-sm font-semibold">Pausa anti-spam em dias</Label>
                    <HelpPopover title="Pausa anti-spam em dias">
                      <p className="text-muted-foreground">
                        É o intervalo mínimo para o mesmo lead entrar de novo nesta campanha. Use para evitar repetição e risco de spam. Exemplo: com 30 dias, o lead só poderá cair novamente nessa campanha depois de 30 dias.
                      </p>
                    </HelpPopover>
                  </div>
                  <Input
                    id="followup-cooldown"
                    type="number"
                    min={0}
                    value={draft.cooldown_dias_relancamento}
                    onChange={(event) => setDraft({ ...draft, cooldown_dias_relancamento: Number(event.target.value) })}
                    className="bg-card"
                  />
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="followup-limite-diario" className="text-sm font-semibold">Limite diário de envios</Label>
                    <HelpPopover title="Limite diário de envios">
                      <p className="text-muted-foreground">
                        Define quantas mensagens esta campanha pode enviar em 24 horas. Use um número baixo no início para testar com segurança antes de ampliar.
                      </p>
                    </HelpPopover>
                  </div>
                  <Input
                    id="followup-limite-diario"
                    type="number"
                    min={1}
                    value={draft.limite_diario}
                    onChange={(event) => setDraft({ ...draft, limite_diario: Number(event.target.value) })}
                    className="bg-card"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="followup-sections space-y-6">
            {/* Seção: Instruções da IA (aparece se houver etapa de IA) */}
            {draft.templates.some((template) => template.gerado_por_ia) && (
              <div className="space-y-3">
                <h3 className="lg-rotulo">Instruções da IA</h3>
                <div className="space-y-4 rounded-2xl border border-border bg-muted p-4">
                  <div>
                    <Label htmlFor="followup-ai-prompt" className="text-sm font-semibold">Prompt base da campanha</Label>
                    <p className="text-xs text-brand-strong">
                      Escolha um estilo pronto ou ajuste o texto. A IA deve seguir o histórico real da conversa.
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {AI_INSTRUCTION_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setDraft({ ...draft, ia_prompt_base: preset.prompt })}
                        aria-pressed={draft.ia_prompt_base === preset.prompt}
                        className={`rounded-2xl border p-3 text-left transition-colors ${
                          draft.ia_prompt_base === preset.prompt
                            ? 'border-ring bg-accent text-foreground'
                            : 'border-input bg-card text-foreground hover:border-ring'
                        }`}
                      >
                        <span className="block text-sm font-semibold">{preset.titulo}</span>
                        <span className="mt-1 block text-xs text-muted-foreground">{preset.descricao}</span>
                      </button>
                    ))}
                  </div>
                  <Textarea
                    id="followup-ai-prompt"
                    value={draft.ia_prompt_base}
                    onChange={(event) => setDraft({ ...draft, ia_prompt_base: event.target.value })}
                    placeholder="Ex: seja breve, acolhedor e retome apenas o assunto real do histórico, sem inventar informações."
                    className="min-h-24 bg-card"
                  />
                  <p className="text-xs text-brand-strong">
                    A IA só gera texto quando um lead estiver apto ao follow-up; ela lê o histórico desse lead antes do envio.
                  </p>
                </div>
              </div>
            )}

            {/* Seção: Sequência de mensagens */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Sequência de mensagens</h3>
              <div className="space-y-4">
                {draft.templates.map((template, index) => {
                  const scheduleExample = getDynamicScheduleExample(
                    draft.janela_inatividade_minutos,
                    template.intervalo_minutos,
                  );
                  const isFirstTemplate = index === 0;

                  return (
                  <div key={index} className="overflow-hidden rounded-3xl border bg-card shadow-[var(--lg-sombra-1)]">
                    {/* Header do card de etapa */}
                    <div className="flex items-center justify-between border-b bg-muted px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-foreground">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-semibold">{template.titulo || `Etapa ${index + 1}`}</p>
                          <p className="text-xs text-muted-foreground">
                            {template.gerado_por_ia ? 'Gerada por IA' : 'Texto manual'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {draft.templates.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="followup-action text-danger-text hover:bg-danger-subtle hover:text-danger-text" aria-label={`Remover mensagem ${index + 1}`} onClick={() => removeTemplate(index)}>
                            <Trash2 className="h-4 w-4 text-danger-text" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4 p-4">
                      {/* Rótulos visíveis permitem identificar os campos depois de preenchidos. */}
                      <div className="grid gap-2">
                        <Label htmlFor={`followup-template-${index}-titulo`}>Título interno da mensagem</Label>
                        <Input
                          id={`followup-template-${index}-titulo`}
                          value={template.titulo}
                          onChange={(event) => updateTemplate(index, { titulo: event.target.value })}
                          placeholder="Ex.: Primeira tentativa"
                          className="bg-card"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5">
                          <p id={`followup-template-${index}-tipo`} className="text-sm font-semibold">Tipo de mensagem</p>
                          <HelpPopover title="Tipo de mensagem desta etapa">
                            <p className="text-muted-foreground">
                              Manual envia exatamente o texto escrito. IA dinâmica gera a mensagem no momento do envio usando o histórico real do lead e as instruções da campanha.
                            </p>
                          </HelpPopover>
                        </div>
                        {/* RadioGroup mantém seleção única e navegação por setas no teclado. */}
                        <RadioGroup
                          value={template.mensagem_tipo}
                          onValueChange={(value: FollowupMensagemTipo) => updateMensagemTipo(index, value)}
                          aria-labelledby={`followup-template-${index}-tipo`}
                          className="grid gap-2 sm:grid-cols-2"
                        >
                          {([
                            { value: 'manual', titulo: 'Manual', descricao: 'Texto fixo escrito por você', icone: PencilLine },
                            { value: 'ai_dinamico', titulo: 'IA dinâmica', descricao: 'Mensagem baseada na conversa', icone: Sparkles },
                          ] as const).map(({ value, titulo, descricao, icone: Icone }) => (
                            <label
                              key={value}
                              htmlFor={`followup-template-${index}-${value}`}
                              className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 text-left text-foreground transition-colors ${template.mensagem_tipo === value ? 'border-ring bg-accent' : 'border-input bg-card hover:bg-muted'}`}
                            >
                              <Icone className="h-5 w-5 shrink-0 text-brand-strong" aria-hidden="true" />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold">{titulo}</span>
                                <span className="block text-xs text-muted-foreground">{descricao}</span>
                              </span>
                              <RadioGroupItem id={`followup-template-${index}-${value}`} value={value} aria-label={titulo} className="shrink-0 border-ring text-foreground" />
                            </label>
                          ))}
                        </RadioGroup>
                      </div>

                      {/* Texto de apoio sem caixa mantém a orientação discreta e evita outra superfície dentro da mensagem. */}
                      {template.gerado_por_ia ? (
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          A mensagem desta etapa será criada pela IA no momento do envio, usando o histórico real do lead e as instruções acima.
                        </p>
                      ) : (
                        <div className="grid gap-2">
                          <Label htmlFor={`followup-template-${index}-conteudo`}>Texto da mensagem</Label>
                          <Textarea
                            id={`followup-template-${index}-conteudo`}
                            value={template.conteudo}
                            onChange={(event) => updateTemplate(index, { conteudo: event.target.value })}
                            placeholder="Escreva aqui a mensagem que será enviada ao lead..."
                            className="min-h-28 bg-card"
                          />
                        </div>
                      )}

                      {oficialAtivo && !template.gerado_por_ia && (
                        <div className="space-y-2 rounded-2xl border border-warning bg-warning-subtle p-3">
                          <Label>Modelo da Meta para quando passar de 24 horas</Label>
                          <Select
                            value={template.template_oficial_id ?? ''}
                            onValueChange={(valor) => updateTemplate(index, { template_oficial_id: valor || undefined })}
                          >
                            <SelectTrigger className="bg-card"><SelectValue placeholder="Opcional — modelo aprovado" /></SelectTrigger>
                            <SelectContent className="gens-escopo rounded-2xl">
                              {templatesAprovados.map((modelo) => (
                                <SelectItem key={modelo.id} value={modelo.id}>{modelo.nome}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!template.template_oficial_id && (
                            <p className="text-xs text-warning-text">
                              Depois de 24 horas este follow-up não sai até você vincular um modelo aprovado.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2 rounded-2xl border border-border bg-muted p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-semibold">Imagem opcional</p>
                              <p className="text-xs text-muted-foreground">
                                Anexe uma imagem para enviar junto com esta mensagem.
                              </p>
                            </div>
                          </div>
                          {template.imagem_object_key && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeTemplateImage(index)}
                              className="text-danger-text hover:bg-danger-subtle hover:text-danger-text"
                            >
                              <X className="mr-1 h-4 w-4" />
                              Remover
                            </Button>
                          )}
                        </div>

                        {template.imagem_object_key ? (
                          <div className="grid gap-3 rounded-2xl border bg-card p-3 sm:grid-cols-[120px_1fr]">
                            <div className="flex h-24 w-full items-center justify-center overflow-hidden rounded-md border bg-muted">
                              {template.imagem_preview_url ? (
                                <img
                                  src={template.imagem_preview_url}
                                  alt="Imagem anexada ao follow-up"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <ImageIcon className="h-8 w-8 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex min-w-0 flex-col justify-center gap-1">
                              <p className="truncate text-sm font-semibold text-foreground">
                                {template.imagem_nome_arquivo || 'Imagem anexada'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Pronta para ser enviada com o follow-up.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <label className="relative flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-input bg-card px-4 py-3 text-sm text-foreground hover:bg-muted focus-within:ring-2 focus-within:ring-ring">
                            <Upload className="h-4 w-4" />
                            {uploadFollowupImage.isPending ? 'Enviando imagem...' : 'Enviar imagem'}
                            {/* O input continua focável para permitir anexar imagem pelo teclado. */}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="sr-only"
                              aria-label={`Enviar imagem para mensagem ${index + 1}`}
                              disabled={uploadFollowupImage.isPending}
                              onChange={(event) => {
                                const file = event.target.files?.[0] || null;
                                void uploadTemplateImage(index, file);
                                event.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>

                      {isFirstTemplate ? (
                        <div className="rounded-2xl border border-border bg-accent p-3 text-xs text-accent-foreground">
                          {draft.modo_envio === 'horario_fixo'
                            ? `Primeira mensagem: envia na próxima verificação após ${draft.horario_envio}, se o lead já estiver apto.`
                            : `Primeira mensagem: envia assim que completar ${minutosParaLegivel(draft.janela_inatividade_minutos)} sem resposta.`}
                        </div>
                      ) : (
                        <div className="space-y-3 rounded-2xl border border-border bg-muted p-3">
                          <div className="flex items-center gap-1.5">
                            <Label className="text-sm font-semibold">Aguardar após a mensagem anterior</Label>
                            <HelpPopover title="Intervalo entre etapas">
                              <p className="text-muted-foreground">
                                Esse tempo começa a contar depois que a etapa anterior foi enviada. Se o lead responder antes, as próximas etapas são canceladas quando a auto-pausa está ligada.
                              </p>
                            </HelpPopover>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <DurationEditor
                              idPrefix={`followup-template-${index}-intervalo`}
                              valueMinutes={template.intervalo_minutos}
                              minMinutes={0}
                              onChangeMinutes={(minutes) => updateTemplate(index, { intervalo_minutos: minutes })}
                            />
                            <div className="flex flex-col justify-center gap-1">
                              <p className="text-sm font-semibold text-foreground">
                                {minutosParaLegivel(template.intervalo_minutos)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Esse intervalo só vale para mensagens adicionais da sequência.
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-brand-strong">
                            Exemplo: se a mensagem anterior saiu às {scheduleExample.entrouNoFollowup},
                            esta etapa envia às {scheduleExample.enviaEtapa}.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )})}
              </div>
            </div>

            <Button type="button" variant="soft" onClick={addTemplate} className="followup-action w-full">
              <Plus className="mr-1 h-4 w-4" />
              Adicionar mensagem à sequência
            </Button>
          </div>
        )}

        {step === 4 && (
          <div className="followup-sections space-y-6">
            {/* Header da revisão */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Resumo da campanha</h3>
              <div className="rounded-2xl border border-border bg-muted p-4">
                <p className="text-lg font-semibold text-foreground">{draft.nome}</p>
                <p className="text-sm text-muted-foreground">{draft.descricao || 'Sem descrição.'}</p>
              </div>
            </div>

            {/* Configurações principais */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Configurações</h3>
              <div className="grid gap-3 rounded-2xl border border-border bg-muted p-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Gatilho</p>
                  <p className="text-sm font-semibold text-foreground">{draft.tipo_gatilho.replace(/_/g, ' ')}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Disparo</p>
                  <p className="text-sm font-semibold text-foreground">{draft.modo_envio === 'horario_fixo' ? `Janela ${draft.horario_envio} até ${draft.horario_limite_envio}` : `Após ${minutosParaLegivel(draft.janela_inatividade_minutos)} até ${draft.horario_limite_envio}`}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Tempo de espera</p>
                  <p className="text-sm font-semibold text-foreground">Entre {minutosParaLegivel(draft.janela_inatividade_minutos)} e {minutosParaLegivel(draft.janela_maxima_inatividade_minutos)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Filtro de anúncio</p>
                  <p className="text-sm font-semibold text-foreground">
                    {draft.apenas_leads_anuncio
                      ? (selectedAdOption?.displayName || draft.ad_name_filtro.trim() || 'Qualquer anúncio')
                      : 'Desativado'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Mensagens</p>
                  <p className="text-sm font-semibold text-foreground">{draft.templates.length} etapa(s)</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Auto-pausa</p>
                  <p className="text-sm font-semibold text-foreground">{draft.auto_pausar_ao_responder ? 'Ativado' : 'Desativado'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Follow-up inteligente</p>
                  <p className="text-sm font-semibold text-foreground">{draft.usar_classificacao_inteligente ? 'Ativado' : 'Desativado'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Limite diário</p>
                  <p className="text-sm font-semibold text-foreground">{draft.limite_diario} envios</p>
                </div>
              </div>
            </div>

            {/* Preview das mensagens */}
            <div className="space-y-3">
              <h3 className="lg-rotulo">Sequência</h3>
              <div className="space-y-2">
                {draft.templates.map((template, index) => (
                  <div key={index} className="flex items-center gap-3 rounded-2xl border border-border bg-muted p-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-foreground">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{template.titulo}</p>
                      <p className="text-xs text-muted-foreground">
                        {template.gerado_por_ia ? 'IA dinâmica' : (template.conteudo ? `${template.conteudo.slice(0, 60)}...` : 'Sem conteúdo')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerta de status */}
            <div className="rounded-2xl border border-border bg-accent p-4 text-sm text-accent-foreground">
              <p className="font-medium">
                {isEditing ? 'Modo edição' : 'Modo criação'}
              </p>
              <p className="mt-1 text-xs">
                {isEditing
                  ? 'As alterações serão aplicadas mantendo a campanha e o histórico existentes.'
                  : 'A campanha será salva inativa para você revisar antes de ligar os disparos automáticos.'}
              </p>
            </div>
          </div>
        )}
          </div>
        </div>

        {/* As ações ficam fora do scroll para continuar acessíveis em telas pequenas. */}
        <div className="shrink-0 border-t bg-card px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            {step === 0 ? (
              // Na escolha de modelo não existe "voltar": a saída é cancelar a criação.
              <Button type="button" variant="ghost" className="followup-action" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="followup-action"
                onClick={() => setStep((current) => Math.max(primeiraEtapa, current - 1))}
                disabled={step === primeiraEtapa}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Voltar
              </Button>
            )}

            {step === 0 ? (
              // Terceiro nível da escolha: sem modelo, a pessoa monta tudo nas próximas etapas.
              <Button type="button" variant="outline" className="followup-action" onClick={comecarDoZero}>
                <PencilLine className="mr-1 h-4 w-4" />
                Começar do zero
              </Button>
            ) : step < 4 ? (
              <Button
                type="button"
                className="followup-action hover:bg-brand-soft"
                onClick={() => setStep((current) => current + 1)}
                disabled={!isStepValid}
              >
                Avançar
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" className="followup-action hover:bg-brand-soft" onClick={handleSave} disabled={createCampaign.isPending || updateCampaign.isPending} aria-busy={createCampaign.isPending || updateCampaign.isPending}>
                {(createCampaign.isPending || updateCampaign.isPending) ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                {(createCampaign.isPending || updateCampaign.isPending) ? 'Salvando...' : 'Salvar campanha'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
